import React, { useState } from "react";
import i18n, { T } from "@/i18n";
import { MicRow, appendText } from "@/components/VoiceMic";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { createOrder } from "@/lib/vendorApi";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { MapPin, Phone, Home, MessageSquare, CheckCircle2 } from "lucide-react";

// Simple uuid-ish generator for idempotency keys (no external dep needed)
const genKey = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export default function CheckoutDialog({ open, onOpenChange }) {
  const { cart, cartTotal, pincode, user, clearCart, formatApiError } = useApp();
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [pin, setPin] = useState(pincode || "");
  const [landmark, setLandmark] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [idemKey] = useState(genKey());

  const shopsInCart = new Set(cart.map((c) => c.shop_name)).size;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error(i18n.t("c.loginFirst")); onOpenChange(false); return; }
    if (cart.length === 0) { toast.error(i18n.t("c.cartEmpty")); return; }
    setSubmitting(true);
    try {
      const res = await createOrder({
        items: cart.map((c) => ({ product_id: c.id, qty: c.qty })),
        delivery_address: address.trim(),
        delivery_pincode: pin.trim(),
        landmark: landmark.trim(),
        phone: phone.trim(),
        notes: notes.trim(),
        idempotency_key: idemKey,
      });
      const orders = res.orders || [];
      toast.success(`${i18n.t("c.placed")}${orders.length > 1 ? ` (${orders.length})` : ""} ${res.idempotent ? "(existing)" : ""}`);
      clearCart();
      onOpenChange(false);
      // Navigate to first order detail if only one, else to my orders
      if (orders.length === 1) navigate(`/orders/${orders[0].id}`);
      else navigate("/orders");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden rounded-2xl max-h-[90vh] flex flex-col" data-testid="checkout-dialog">
        <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 text-white px-6 pt-6 pb-5">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-display font-bold text-white"><T k="c.title" /></DialogTitle>
          </DialogHeader>
          <p className="text-sm text-emerald-100 mt-1">
            {cart.length} item{cart.length > 1 ? "s" : ""} · ₹{cartTotal}
            {shopsInCart > 1 && <span className="block mt-1 text-xs">Items from {shopsInCart} shops — one order per shop will be created.</span>}
          </p>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3 overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Home className="w-3.5 h-3.5" /> <T k="c.address" /></label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required minLength={5} maxLength={300}
              rows={2}
              placeholder={i18n.t("c.addressPh")}
              className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500"
              data-testid="checkout-address"
            />
            <MicRow onText={(t) => setAddress((v) => appendText(v, t).slice(0, 300))} testid="mic-checkout-address" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider"><T k="c.pincode" /></label>
              <input
                value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required minLength={6} maxLength={6}
                className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500"
                data-testid="checkout-pincode"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> <T k="c.phone" /></label>
              <input
                value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                required minLength={10}
                className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500"
                data-testid="checkout-phone"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> <T k="c.landmark" /></label>
            <input
              value={landmark} onChange={(e) => setLandmark(e.target.value)} maxLength={100}
              placeholder="Near park / school"
              className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500"
              data-testid="checkout-landmark"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> <T k="c.notes" /></label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={300} rows={2}
              placeholder="e.g. Please pack sweets separately"
              className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500"
              data-testid="checkout-notes"
            />
          </div>

          <div className="bg-slate-50 rounded-xl p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-600"><T k="c.subtotal" /></span><span className="font-semibold">₹{cartTotal}</span></div>
            <div className="flex justify-between"><span className="text-slate-600"><T k="c.delivery" /></span><span className="font-semibold text-emerald-700"><T k="c.deliveryPer" /></span></div>
            <div className="flex justify-between pt-1 border-t border-slate-200"><span className="font-semibold"><T k="c.payment" /></span><span className="font-semibold"><T k="c.cod" /></span></div>
          </div>

          <button type="submit" disabled={submitting} className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2" data-testid="place-order-btn">
            <CheckCircle2 className="w-4 h-4" /> {submitting ? i18n.t("c.placing") : `${i18n.t("c.place")} · ₹${cartTotal}+`}
          </button>
          <p className="text-[10px] text-center text-slate-400"><T k="c.prototype" /></p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
